import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';

import type { GetCurrenciesResponse } from '@/api/config/getCurrencies.ts';
import { selectCurrenciesQueryResult } from '@/api/config/selectors.ts';
import { userApi } from '@/api/user/index.ts';
import { getEspPrices } from '@/store/cash/selectors/esp.ts';
import type { CashPriceStatus } from '@/store/cash/selectors/prices.types.ts';
import { getRfsPrices } from '@/store/cash/selectors/rfs.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import type { FxCash, FxCashNdf, FxCashSpot } from '@/store/cash/state.ts';
import type { EspStream } from '@/store/esp/state.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import type { AppState } from '@/store/store.ts';
import { createWeakAppSelector } from '@/store/storeMethods.ts';
import { getIsStreamingConnected, getIsStreamingUnusable } from '@/store/streaming/slice.ts';
import { selectTabClientIdOfTile } from '@/store/tiles/selectors.ts';
import { selectWorkspaceCanTrade } from '@/store/workspace/slice.ts';

export const selectCashPriceStatus = createWeakAppSelector(
  // tile
  (_state: AppState, tileId: string) => tileId,
  (state: AppState, tileId: string) => selectCashTile(state, tileId),

  // client ID
  selectTabClientIdOfTile,

  // ESP limits
  (state: AppState) => state.esp.limits,

  // ESP streams
  (state: AppState) => state.esp.streams,

  // configuration of currencies
  (state: AppState) => selectCurrenciesQueryResult(state),

  // user type
  (state: AppState) => userApi.endpoints.currentUser.select()(state).data?.userType === 'Internal Sales',

  // user level clearance for trading
  (state: AppState) => userApi.endpoints.currentUser.select()(state).data?.canTrade ?? false,

  // workspace level lock state
  (state: AppState) => selectWorkspaceCanTrade(state),

  (state: AppState) => getIsStreamingUnusable(state),

  // SignalR is up: without it no new price can reach the browser, and an execution would be fired
  // without ever getting its ReceiveDealReply back (executeCashEsp.ts / executeSwapRfs.ts listen on SignalR)
  (state: AppState) => getIsStreamingConnected(state),

  // result
  (
    tileId,
    tile,
    defaultClientId,
    espLimits,
    streams,
    dataByCurrencyPair,
    isInternalSale,
    userCanTrade,
    workspaceCanTrade,
    isStreamingUnusable,
    isStreamingConnected,
  ): CashPriceStatus => {
    const canTrade = userCanTrade && workspaceCanTrade;

    if (isNotDefined(tile) || isNotDefined(tile.properties.currencyPair) || isNotDefined(dataByCurrencyPair.data)) {
      return {
        status: 'idle' as const,
      };
    }

    switch (tile.type) {
      case 'FxSpot':
        return getCashSpotPriceStatus({
          tileId,
          tile,
          defaultClientId,
          espLimits,
          streams,
          dataByCurrencyPair: dataByCurrencyPair.data,
          isInternalSale,
          canTrade,
          isStreamingUnusable,
          isStreamingConnected,
        });

      case 'FxFwd':
        return getCashRfsPriceStatus({
          tile,
          dataByCurrencyPair: dataByCurrencyPair.data,
          isInternalSale,
          keepEsp: false,
          isInvalidPrices: false,
          canTrade,
          isStreamingConnected,
        });

      case 'FxNdf':
        return getCashNdfPriceStatus({
          tile,
          dataByCurrencyPair: dataByCurrencyPair.data,
          isInternalSale,
          canTrade,
          isStreamingConnected,
        });

      default:
        return assertUnreachable(tile, `Unhandled cash product type: ${(tile as FxCash).type}`);
    }
  },
);

// ██████╗ ███████╗███████╗██████╗  ██████╗ ███╗   ██╗███████╗███████╗    ████████╗██╗   ██╗██████╗ ███████╗███████╗
// ██╔══██╗██╔════╝██╔════╝██╔══██╗██╔═══██╗████╗  ██║██╔════╝██╔════╝    ╚══██╔══╝╚██╗ ██╔╝██╔══██╗██╔════╝██╔════╝
// ██████╔╝█████╗  ███████╗██████╔╝██║   ██║██╔██╗ ██║███████╗█████╗         ██║    ╚████╔╝ ██████╔╝█████╗  ███████╗
// ██╔══██╗██╔══╝  ╚════██║██╔═══╝ ██║   ██║██║╚██╗██║╚════██║██╔══╝         ██║     ╚██╔╝  ██╔═══╝ ██╔══╝  ╚════██║
// ██║  ██║███████╗███████║██║     ╚██████╔╝██║ ╚████║███████║███████╗       ██║      ██║   ██║     ███████╗███████║
// ╚═╝  ╚═╝╚══════╝╚══════╝╚═╝      ╚═════╝ ╚═╝  ╚═══╝╚══════╝╚══════╝       ╚═╝      ╚═╝   ╚═╝     ╚══════╝╚══════╝

// ███████╗██████╗  ██████╗ ████████╗
// ██╔════╝██╔══██╗██╔═══██╗╚══██╔══╝
// ███████╗██████╔╝██║   ██║   ██║
// ╚════██║██╔═══╝ ██║   ██║   ██║
// ███████║██║     ╚██████╔╝   ██║
// ╚══════╝╚═╝      ╚═════╝    ╚═╝

type GetCashSpotPriceStatusParams = {
  tileId: string;
  tile: FxCashSpot | FxCashNdf;
  defaultClientId: number | undefined;
  espLimits: Record<string, number>;
  streams: Record<string, EspStream>;
  dataByCurrencyPair: GetCurrenciesResponse;
  isInternalSale: boolean;
  canTrade: boolean;
  isStreamingUnusable: boolean;
  isStreamingConnected: boolean;
};

function getCashSpotPriceStatus(params: GetCashSpotPriceStatusParams): CashPriceStatus {
  const {
    tileId,
    tile,
    defaultClientId,
    espLimits,
    streams,
    dataByCurrencyPair,
    isInternalSale,
    canTrade,
    isStreamingUnusable,
    isStreamingConnected,
  } = params;

  const isCurrencyPairInverted =
    (isDefined(tile.properties.currencyPair) && dataByCurrencyPair.dict[tile.properties.currencyPair]?.isInverted) ??
    false;

  const isRfsStreamActive = isDefined(tile.properties.rfs);

  if (isCurrencyPairInverted || isRfsStreamActive) {
    return getCashRfsPriceStatus({
      tile,
      dataByCurrencyPair,
      isInternalSale,
      keepEsp: true,
      isInvalidPrices: false,
      canTrade,
      isStreamingConnected,
    });
  }

  if (
    isNotDefined(tile.properties.notionalCurrencyIndex) ||
    isNotDefined(tile.properties.maturityDate) ||
    isNotDefined(tile.properties.currencyPair) ||
    isNotDefined(defaultClientId)
  ) {
    return {
      status: 'esp-not-ready' as const,
    };
  }

  if (isNotDefined(tile.properties.margin)) {
    return {
      status: 'esp-tiering-required' as const,
      clientId: tile.clientId ?? defaultClientId,
      currencyPair: tile.properties.currencyPair,
      maturityDate: tile.properties.maturityDate,
    };
  }

  if (tile.properties.margin.isTieringSuccess === false) {
    return getCashRfsPriceStatus({
      tile,
      dataByCurrencyPair,
      isInternalSale,
      keepEsp: true,
      isInvalidPrices: false,
      canTrade,
      isStreamingConnected,
    });
  }

  const espId = getEspStreamId(tile.properties.currencyPair, tile.properties.margin.tiering);
  const espStream = streams[espId];

  if (isNotDefined(espStream) || !espStream.subscribedTileIds.includes(tileId)) {
    return {
      status: 'esp-required' as const,
      currencyPair: tile.properties.currencyPair,
      tiering: tile.properties.margin.tiering,
      clientId: tile.clientId ?? defaultClientId,
    };
  }

  if (espStream.status === 'PENDING') {
    return {
      status: 'esp-pending' as const,
      tiering: tile.properties.margin.tiering,
    };
  }

  const currencyPairPrecision = dataByCurrencyPair.dict[tile.properties.currencyPair]?.precision ?? 5;

  const notionalCurrency =
    tile.properties.notionalCurrencyIndex === 1 ? tile.properties.currency1 : tile.properties.currency2;

  const espLimitForCurrency = isDefined(notionalCurrency) ? espLimits[notionalCurrency] : undefined;

  if (
    isDefined(espLimitForCurrency) &&
    isDefined(tile.properties.notional) &&
    tile.properties.notional > espLimitForCurrency
  ) {
    return getCashRfsPriceStatus({
      tile,
      dataByCurrencyPair,
      isInternalSale,
      keepEsp: true,
      isInvalidPrices: false,
      canTrade,
      isStreamingConnected,
    });
  }

  const prices = getEspPrices({
    espPrice: espStream,
    notional: tile.properties.notional,
    notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
    currencyPair: tile.properties.currencyPair,
    currencyPairPrecision,
    margin: tile.properties.margin,
    isInternalSale,
    isStreamingUnusable,
    canTrade,
  });

  // notional is over the latest ladder => no ladder
  if (prices.status === 'no-ladder') {
    return getCashRfsPriceStatus({
      tile,
      dataByCurrencyPair,
      isInternalSale,
      keepEsp: true,
      isInvalidPrices: false,
      canTrade,
      isStreamingConnected,
    });
  }

  // prices is invalid
  if (prices.status === 'invalid-prices') {
    return getCashRfsPriceStatus({
      tile,
      dataByCurrencyPair,
      isInternalSale,
      keepEsp: true,
      isInvalidPrices: true,
      canTrade,
      isStreamingConnected,
    });
  }

  return prices;
}

// ███████╗ ██████╗ ██████╗ ██╗    ██╗ █████╗ ██████╗ ██████╗
// ██╔════╝██╔═══██╗██╔══██╗██║    ██║██╔══██╗██╔══██╗██╔══██╗
// █████╗  ██║   ██║██████╔╝██║ █╗ ██║███████║██████╔╝██║  ██║
// ██╔══╝  ██║   ██║██╔══██╗██║███╗██║██╔══██║██╔══██╗██║  ██║
// ██║     ╚██████╔╝██║  ██║╚███╔███╔╝██║  ██║██║  ██║██████╔╝
// ╚═╝      ╚═════╝ ╚═╝  ╚═╝ ╚══╝╚══╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝

type GetCashRfsPriceStatusParams = {
  tile: FxCash;
  dataByCurrencyPair: GetCurrenciesResponse;
  isInternalSale: boolean;
  /** keep the ESP stream subscribed while the RFS is the price source */
  keepEsp: boolean;
  isInvalidPrices: boolean;
  /** user level clearance AND workspace unlocked */
  canTrade: boolean;
  isStreamingConnected: boolean;
};

export function getCashRfsPriceStatus(params: GetCashRfsPriceStatusParams): CashPriceStatus {
  const { tile, dataByCurrencyPair, isInternalSale, keepEsp, isInvalidPrices, canTrade, isStreamingConnected } = params;

  if (
    isNotDefined(tile.properties.currencyPair) ||
    isNotDefined(tile.properties.notional) ||
    isNotDefined(tile.properties.notionalCurrencyIndex) ||
    isNotDefined(tile.properties.maturityDate)
  ) {
    return {
      status: 'rfs-not-ready' as const,
      keepEsp,
      isInvalidPrices,
    };
  }

  const [currency1, currency2] = tile.properties.currencyPair.split('/');
  const currencyPairData = dataByCurrencyPair.dict[tile.properties.currencyPair];

  if (isNotDefined(currencyPairData) || isNotDefined(currency1) || isNotDefined(currency2)) {
    return {
      status: 'rfs-not-ready' as const,
      keepEsp,
      isInvalidPrices,
    };
  }

  const currencyPairPrecision = currencyPairData.precision;

  if (isNotDefined(tile.properties.rfs)) {
    return {
      status: 'rfs-required' as const,
      currencyPair: tile.properties.currencyPair,
      maturityDate: tile.properties.maturityDate,
      keepEsp,
    };
  }

  if (tile.tradeCapture.status === 'CHECKING' || tile.properties.rfs.status === 'PENDING') {
    return {
      status: 'rfs-pending' as const,
      currencyPair: tile.properties.currencyPair,
      maturityDate: tile.properties.maturityDate,
      keepEsp,
    };
  }

  return getRfsPrices({
    currency1,
    currency2,
    rfs: tile.properties.rfs,
    currencyPairPrecision,
    notional: tile.properties.notional,
    notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
    isInternalSale,
    keepEsp,
    canTrade,
    isStreamingConnected,
  });
}

// ███╗   ██╗██████╗ ███████╗
// ████╗  ██║██╔══██╗██╔════╝
// ██╔██╗ ██║██║  ██║█████╗
// ██║╚██╗██║██║  ██║██╔══╝
// ██║ ╚████║██████╔╝██║
// ╚═╝  ╚═══╝╚═════╝ ╚═╝

type GetCashNdfPriceStatusParams = {
  tile: FxCashNdf;
  dataByCurrencyPair: GetCurrenciesResponse;
  isInternalSale: boolean;
  /** user level clearance AND workspace unlocked */
  canTrade: boolean;
  isStreamingConnected: boolean;
};

function getCashNdfPriceStatus(params: GetCashNdfPriceStatusParams): CashPriceStatus {
  const { tile, dataByCurrencyPair, isInternalSale, canTrade, isStreamingConnected } = params;

  return getCashRfsPriceStatus({
    tile,
    dataByCurrencyPair,
    isInternalSale,
    keepEsp: false,
    isInvalidPrices: false,
    canTrade,
    isStreamingConnected,
  });
}
